import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "./Star";
import { ArticlePopover } from "./ArticlePopover";
import { fetchArticles } from "../services/articleService";
import { fetchTopics } from "../services/topicService";
import SearchBar from "./SearchBar";
import level1 from "../assets/level1.png";
import level2 from "../assets/level2.png";
import level3 from "../assets/level3.png";
import level4 from "../assets/level4.png";
import level5 from "../assets/level5.png";

const SkyContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const AppContainer = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background-color: #000; /* Fallback for debugging */

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/pexels-haugsveenphoto-1361215.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: brightness(0.8);
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const LevelImage = styled.img`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 60px;
  height: 60px;
  cursor: pointer;
  transition: transform 0.2s;
  z-index: 2;

  &:hover {
    transform: scale(1.1);
  }
`;

const LevelPopover = styled(motion.div)`
  position: absolute;
  top: 90px;
  left: 20px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 8px;
  padding: 16px;
  z-index: 3;
  min-width: 200px;
`;

const LevelOption = styled.div`
  color: white;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  ${props => props.selected && `
    background-color: rgba(255, 255, 255, 0.2);
  `}
`;

const randomNumber = () => Math.floor(Math.random() * (100 - 10 + 1)) + 10;

const levelImages = {
  'Level 1': level1,
  'Level 2': level2,
  'Level 3': level3,
  'Level 4': level4,
  'Level 5': level5,
};

export const StarrySky = () => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articles, setArticles] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('Level 1');
  const [showLevelPopover, setShowLevelPopover] = useState(false);

  const adjustPosition = (originalY) => {
    return originalY * 0.8;
  };

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const [fetchedArticles, fetchedTopics] = await Promise.all([
          fetchArticles(),
          fetchTopics()
        ]);

        if (isMounted) {
          const processedArticles = fetchedArticles.data.map((a, key) => ({
            ...a,
            position: { x: randomNumber(), y: adjustPosition(randomNumber()) },
            size: 4,
            brightness: 0.9,
            glow: 2,
            twinkleSpeed: 3,
            image: a.image,
          }));
          setArticles(processedArticles);
          setTopics(fetchedTopics.data);
        }
      } catch (err) {
        if (isMounted) setError("Failed to load data.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleTopicChange = (topicId) => {
    setSelectedTopic(topicId);
  };

  const getStarSize = (article) => {
    if (!selectedTopic) return 4;
    console.log({article})
    return article.topics.some(a => `${a.id}` === `${selectedTopic}`) ? 5.2 : 1.2; // 30% of original size for non-matching stars
  };

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    setShowLevelPopover(false);
  };

  const shouldShowStar = (article) => {
    console.log(article.levels)
    return article.levels && article.levels.includes(selectedLevel);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <AppContainer>
      <LevelImage 
        src={levelImages[selectedLevel]} 
        alt={`Level ${selectedLevel}`}
        onClick={() => setShowLevelPopover(!showLevelPopover)}
      />
      
      <AnimatePresence>
        {showLevelPopover && (
          <LevelPopover
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5'].map((level) => (
              <LevelOption
                key={level}
                selected={level === selectedLevel}
                onClick={() => handleLevelSelect(level)}
              >
                {level}
              </LevelOption>
            ))}
          </LevelPopover>
        )}
      </AnimatePresence>

      <SearchBar 
        topics={topics}
        selectedTopic={selectedTopic}
        onTopicChange={handleTopicChange}
      />
      <SkyContainer>
        {articles.map((article) => (
          shouldShowStar(article) && (
            <Star
              key={article.id}
              article={{
                ...article,
                size: getStarSize(article)
              }}
              onClick={() => setSelectedArticle(article)}
            />
          )
        ))}

        <AnimatePresence>
          {selectedArticle && (
            <ArticlePopover
              article={selectedArticle}
              onClose={() => setSelectedArticle(null)}
            />
          )}
        </AnimatePresence>
      </SkyContainer>
    </AppContainer>
  );
};
